/**
 * Client for the lexicon matching worker (VE-24948 / U5, ADR-001 + ADR-002).
 *
 * Mirrors `burnInWorkerClient`'s promise-based, id-correlated shape — the
 * pattern this repo's README names as the one to copy — rather than inventing a
 * fourth worker client style.
 *
 * A page's WAIT and a page's CHARGE are separate quantities here — the wait
 * adds a one-off startup allowance the entries are not billed for, and the
 * charge is capped independently of the entry count. Conflating them broke the
 * tier hierarchy in both directions (review-gate B2): see
 * `PAGE_STARTUP_ALLOWANCE_MS` and `DEADLINE_ENTRY_CAP`.
 *
 * THIS FILE OWNS ALL THREE ADR-001 BUDGETS, and the worker owns none:
 *
 *   per entry-page       50 ms   the page is unsearched for the entries that
 *                                did not report it; an entry that timed out
 *                                ALONE (bisected down to it, or for a one-entry
 *                                run re-run on a worker that still answers) is
 *                                abandoned and named at once
 *   per entry MEAN page  50 ms   abandon the entry, NAME it (BR-47), keep going
 *   whole apply          30 s    stop, report what completed
 *
 * Tier 2 is charged the worker's per-entry compute time (`entryMs`), not wall
 * clock, so main-thread stalls cannot abandon a healthy entry (VE-29502).
 *
 * Tier 2 is a RATE, not a total. A total is a budget on how long an entry has
 * existed, so it is exhausted by page count alone and a cheap literal term died
 * partway through a long document — see `MATCH_BUDGET_ENTRY_MEAN_PAGE_MS`.
 *
 * Only the client sees a whole apply, so only the client can enforce the
 * aggregate tiers. Splitting enforcement across both sides would produce two
 * half-implementations that disagree about whose budget fired.
 *
 * The budgets are enforced by TERMINATING the worker, because a cooperative
 * check cannot interrupt a single `RegExp.exec`. That is safe precisely because
 * the worker is stateless and disposable — see its file header.
 *
 * `signal` stops a run early the same way (terminate).
 *
 * `createWorker` is injectable for one concrete reason beyond taste:
 * `new Worker(new URL(...))` does not work under jsdom, and this repo's jest
 * moduleNameMapper only intercepts `worker-loader`. Injecting the factory is
 * what makes the budget behaviour assertable in a plain unit test, with a fake
 * worker and no pathological regex needed.
 */

import {
  LexiconMatchDone,
  LexiconMatchPage,
  LexiconMatchProgress,
  LexiconMatchRequest,
  LexiconMatchResponse,
  LexiconMatchSpan,
} from './lexiconMatchWorker';
import { LexiconEntry } from '@redact-modules/lexicons/models';
import {
  MATCH_BUDGET_APPLY_MS,
  MATCH_BUDGET_ENTRY_MEAN_PAGE_MS,
  MATCH_BUDGET_ENTRY_PAGE_MS,
  MATCH_RATE_MIN_PAGES,
} from '@redact-modules/lexicons/limits';

/** Minimal surface this client needs, so a fake is trivial to write. */
export interface MatchWorkerLike {
  postMessage(message: LexiconMatchRequest): void;
  addEventListener(
    type: 'message',
    listener: (event: MessageEvent<LexiconMatchResponse>) => void
  ): void;
  addEventListener(type: 'error', listener: (event: unknown) => void): void;
  /**
   * Required so `runOnePage` can detach.
   *
   * Without it every page of an apply left a live listener on the surviving
   * worker, each retaining its request closure — so the run held every page's
   * text, and each response fanned out across all of them (review-gate M3).
   */
  removeEventListener(
    type: 'message',
    listener: (event: MessageEvent<LexiconMatchResponse>) => void
  ): void;
  removeEventListener(type: 'error', listener: (event: unknown) => void): void;
  terminate(): void;
}

export type MatchWorkerFactory = () => MatchWorkerLike;

const defaultFactory: MatchWorkerFactory = () =>
  new Worker(new URL('./lexiconMatchWorker.ts', import.meta.url), {
    type: 'module',
  }) as unknown as MatchWorkerLike;

/**
 * Entries beyond which a page's deadline stops growing (review-gate B2).
 *
 * The deadline is a RESPONSE deadline, so it has to be generous enough for a
 * healthy batch and tight enough that a timeout is cheap — only a cheap
 * timeout lets a pathological entry accumulate against the per-entry total and
 * so be NAMED (BR-47).
 */
const DEADLINE_ENTRY_CAP = 8;

/**
 * One-off allowance for `postMessage` structured-clone plus worker startup, ms.
 *
 * Added to how long we WAIT, never to what entries are CHARGED — those are
 * different quantities and conflating them is a defect in both directions.
 * Without the allowance a one-entry lexicon got `entryPageMs` (50 ms) to cover
 * boot, clone and matching, and timed out on nothing pathological. Charging it
 * would be worse: startup is a one-off, and billing it as though it were the
 * entry's own cost skews the tier-2 mean hardest on the shortest documents —
 * the silent-zero shape NFR-S3 forbids. `chargeEntry` excludes an entry's first
 * page from the mean for the same reason.
 */
const PAGE_STARTUP_ALLOWANCE_MS = 400;

export interface LexiconMatchOutcome {
  readonly matches: readonly LexiconMatchSpan[];
  /**
   * Entries abandoned because they threw or blew a budget. BR-47 requires these
   * be named to the user rather than silently dropped.
   */
  readonly abandonedEntryIds: readonly string[];
  /**
   * True when a BUDGET stopped work early — either the whole-apply budget or at
   * least one page's response deadline.
   *
   * It no longer doubles as "the worker died"; see `crashed`. Conflating the two
   * meant a worker-chunk 404 — this repo's documented prod-only breakage point
   * — was recorded in a legal-release audit trail as a performance event, told
   * the reviewer to "Apply again", and masked a deployment fault as slowness
   * (review-gate #3 BR-12).
   */
  readonly timedOut: boolean;
  /** True when the worker died on at least one page. */
  readonly crashed: boolean;
  /** Pages that reported before the run ended. */
  readonly pagesMatched: readonly number[];
  /** Pages whose response deadline elapsed with no answer. */
  readonly timedOutPages: readonly number[];
  /**
   * Pages whose worker died before reporting.
   *
   * Returned, not merely collected. It used to be written and never read — the
   * incomplete FLAG survived but the page's identity did not, so no surface
   * could say which page went unsearched, which is the one fact needed to
   * decide between re-applying and re-opening (review-gate #3 BR-11).
   */
  readonly crashedPages: readonly number[];
  /**
   * Pages never sent because the run stopped first (apply budget, or every
   * entry abandoned). Reported as unsearched. Not set on abort.
   */
  readonly unreachedPages?: readonly number[];
  /** `signal` stopped the run. The outcome is partial: discard it. */
  readonly aborted?: boolean;
}

export interface RunLexiconMatchArgs {
  readonly requestId: string;
  readonly entries: readonly LexiconEntry[];
  readonly pages: readonly LexiconMatchPage[];
  /** Called as each page reports, so the panel can stream (NFR-U5-5). */
  readonly onProgress?: (progress: LexiconMatchProgress) => void;
  readonly createWorker?: MatchWorkerFactory;
  /** Overridable for tests; production uses the ADR-001 values. */
  readonly budgets?: {
    readonly entryPageMs?: number;
    readonly entryMeanPageMs?: number;
    readonly rateMinPages?: number;
    readonly applyMs?: number;
  };
  /** Injectable clock so budget behaviour is testable without real waiting. */
  readonly now?: () => number;
  /** Aborts the run: sends no more pages and terminates the worker. */
  readonly signal?: AbortSignal;
}

/**
 * Runs one apply, page by page, under the three budgets.
 *
 * Pages are dispatched one per message (ADR-002's batch granularity) so a
 * termination costs at most one page's unreported work.
 *
 * Entries that exhaust their aggregate budget are dropped from SUBSEQUENT pages
 * rather than retried — that is what stops one pathological pattern consuming
 * the whole apply while still naming it.
 */
export async function runLexiconMatch(
  args: RunLexiconMatchArgs
): Promise<LexiconMatchOutcome> {
  const {
    requestId,
    entries,
    pages,
    onProgress,
    createWorker = defaultFactory,
    now = () => Date.now(),
    signal,
  } = args;

  // Already aborted: don't start a worker.
  if (signal?.aborted) {
    return {
      matches: [],
      abandonedEntryIds: [],
      timedOut: false,
      crashed: false,
      pagesMatched: [],
      timedOutPages: [],
      crashedPages: [],
      aborted: true,
    };
  }

  const entryPageMs = args.budgets?.entryPageMs ?? MATCH_BUDGET_ENTRY_PAGE_MS;
  const entryMeanPageMs =
    args.budgets?.entryMeanPageMs ?? MATCH_BUDGET_ENTRY_MEAN_PAGE_MS;
  const rateMinPages = args.budgets?.rateMinPages ?? MATCH_RATE_MIN_PAGES;
  const applyMs = args.budgets?.applyMs ?? MATCH_BUDGET_APPLY_MS;

  const matches: LexiconMatchSpan[] = [];
  const abandoned = new Set<string>();
  const pagesMatched: number[] = [];
  const timedOutPages: number[] = [];
  /** Time each entry has spent on its MEASURED pages (see `chargeEntry`). */
  const entrySpend = new Map<string, number>();
  /** How many pages each entry has been charged for, measured or not. */
  const entryPagesCharged = new Map<string, number>();

  const applyStart = now();
  let timedOut = false;
  let crashed = false;
  let aborted = false;
  let unreachedPages: number[] = [];
  /** Pages whose worker died before reporting. Returned in the outcome. */
  const crashedPages: number[] = [];

  /**
   * Charge one entry for one page, and re-judge its RATE (tier 2).
   *
   * THE FIRST CHARGED PAGE IS NOT MEASURED. It carries regex compile and JIT
   * warm-up (without `entryMs`, also worker module load and the first
   * structured clone) — the same one-off `PAGE_STARTUP_ALLOWANCE_MS`
   * exists to wait for without billing anyone. Including it, a two-page
   * document where page 1 cost 300 ms of boot and page 2 cost half a
   * millisecond has a mean of 150 ms, and a perfectly healthy entry is
   * abandoned on arithmetic that describes the worker rather than the pattern.
   *
   * A MEAN, NOT A TOTAL, and the distinction is the whole fix. A total is a
   * budget on how long the entry has been alive, so it is exhausted by page
   * count alone: on a 1,602-page document a literal term was abandoned
   * partway through and the rest of the file was never searched. A mean does
   * not move with document length.
   *
   * `> `, not `>= `, so an entry charged exactly the threshold — which is what
   * a crashed page charges — is not abandoned for a fault that is not its own.
   */
  const chargeEntry = (entryId: string, ms: number): void => {
    const charged = (entryPagesCharged.get(entryId) ?? 0) + 1;
    entryPagesCharged.set(entryId, charged);
    if (charged === 1) {
      return;
    }
    const spent = (entrySpend.get(entryId) ?? 0) + ms;
    entrySpend.set(entryId, spent);
    const measuredPages = charged - 1;
    if (
      measuredPages >= rateMinPages &&
      spent / measuredPages > entryMeanPageMs
    ) {
      abandoned.add(entryId);
    }
  };

  /** An entry's worker-measured cost; 0 when absent or not a finite number. */
  const measured = (
    entryMs: Readonly<Record<string, number>> | undefined,
    entryId: string
  ): number => {
    const ms = entryMs?.[entryId];
    return typeof ms === 'number' && Number.isFinite(ms) ? ms : 0;
  };

  /**
   * Charge one incomplete page to a group of entries.
   *
   * A FLAT `entryPageMs` each, deliberately not `pageWaitBudgetMs / N`.
   * Dividing the capped wait by the entry count made the charge shrink as the
   * lexicon grew — 50 ms at 8 entries, 0.08 ms at 5,000 — so at the NFR-U5-7
   * ceiling no entry's mean could ever rise and BR-47 could not name the
   * pattern responsible. The callers now pass what the page actually cost — a
   * timeout charges its deadline, a crash charges exactly the threshold so it
   * can never abandon anything on its own.
   *
   * The group passed in matters enormously, and used to be every active entry
   * — see `isolateSlowEntries`.
   */
  const chargePageToActiveEntries = (
    active: readonly LexiconEntry[],
    ms: number
  ): void => {
    for (const entry of active) {
      chargeEntry(entry.entryId, ms);
    }
  };

  let worker = createWorker();
  let disposed = false;

  const dispose = () => {
    if (!disposed) {
      disposed = true;
      worker.terminate();
    }
  };

  /** Terminate the current worker and stand up a fresh one. */
  const respawn = (): void => {
    dispose();
    disposed = false;
    worker = createWorker();
  };

  /** Wall-clock left in the whole-apply budget. */
  const applyRemainingMs = (): number => applyMs - (now() - applyStart);

  /** A group's response deadline: tier 1 x its size, capped and floored. */
  const fullDeadlineForGroup = (size: number): number =>
    Math.max(
      entryPageMs * Math.min(size, DEADLINE_ENTRY_CAP),
      PAGE_STARTUP_ALLOWANCE_MS
    );
  const deadlineForGroup = (size: number): number =>
    Math.min(fullDeadlineForGroup(size), Math.max(1, applyRemainingMs()));
  /**
   * Did the apply budget cut this deadline short? Then a timeout under it is
   * the budget's, not the group's: blaming it would name a healthy entry and
   * hide the stop. The one rule for every verdict below.
   */
  const budgetCut = (deadlineMs: number, size: number): boolean =>
    deadlineMs < fullDeadlineForGroup(size);

  /**
   * What bisection found on a timed-out page. `attributed` only when an entry
   * timed out ALONE: that entry is the culprit (for a one-entry run, only after
   * `confirmLoneTimeout` re-ran it on a worker that answers). Otherwise
   * `entries` are those that did not report — the page is the problem, not
   * them.
   */
  interface Isolation {
    entries: readonly LexiconEntry[];
    attributed: boolean;
    crashed: boolean;
    /**
     * The worker could not answer even a run with no entries, so the timeout
     * is the worker's. Charged like a crash (never abandons on its own), so a
     * dead worker is not pinned on a lone entry by tier 2 either (VE-29686).
     */
    workerAtFault?: boolean;
    /** The last run answered, so the worker is fit for the next page. */
    workerLive?: boolean;
  }

  /** A sub-run that answered: its group is healthy AND its work is real. */
  const keepHealthyRun = (
    progress: LexiconMatchProgress,
    group: readonly LexiconEntry[],
    found: LexiconMatchSpan[],
    searched: Set<string>
  ) => {
    found.push(...progress.matches);
    for (const e of group) {
      searched.add(e.entryId);
      // Its own measured cost, so a pattern slow but under the deadline is
      // still caught by tier 2 rather than draining the apply budget.
      chargeEntry(e.entryId, measured(progress.entryMs, e.entryId));
    }
    for (const failedId of progress.failedEntryIds) {
      abandoned.add(failedId);
    }
  };

  /**
   * Find WHICH entries made a page time out, by bisecting the batch.
   *
   * WHY THIS EXISTS. The previous design charged a flat `entryPageMs` to every
   * active entry on an incomplete page, so all of them crossed
   * the tier-2 threshold on the SAME page. After five incomplete pages
   * `active` was empty, the loop broke, and the remaining pages were never
   * attempted. At the NFR-U5-7 ceiling the arithmetic is unambiguous: 5,000
   * entries against one page inside a 400 ms floor is not plausible, so pages
   * 1-5 time out, the whole lexicon is abandoned, and the apply returns zero
   * matches with all 5,000 entries named. Naming everything is informationally
   * identical to naming nothing, so BR-47's "name the failing entry" was unmet
   * BY CONSTRUCTION — in both the old `/N` design (unreachable) and the flat one
   * (non-discriminating). It also was not ADR-001 as written, whose tier 1 is
   * "abandon that page FOR THAT ENTRY" (review-gate #3 BR-8).
   *
   * IT ONLY RECURSES WHILE THE SLOWNESS IS ATTRIBUTABLE. If exactly one half
   * times out, the culprit is in there and the other half's matches are kept.
   * If BOTH halves time out, no single entry explains it — the page is the
   * problem (or the worker is), so splitting further would be a false lead as
   * well as expensive: bisecting all the way down against a dead worker costs
   * O(N) real deadline waits, which at the entry ceiling is minutes. Stopping
   * there keeps this O(log N) in the attributable case and O(1) in the
   * unattributable one, and hands the caller back the entries that did not
   * report, so the flat charge still converges on tier 2.
   *
   * Needs no change inside the worker, and pays off twice: the reviewer gets a
   * real name, and A HEALTHY HALF'S MATCHES ARE KEPT rather than the whole page
   * being lost. `found` collects those, so narrowing never throws work away.
   */
  const isolateSlowEntries = async (
    page: LexiconMatchPage,
    group: readonly LexiconEntry[],
    found: LexiconMatchSpan[],
    /** Entry ids whose sub-run DID report this page. */
    searched: Set<string>
  ): Promise<Isolation> => {
    const unattributed = (crashed = false): Isolation => ({
      entries: group.filter((e) => !searched.has(e.entryId)),
      attributed: false,
      crashed,
    });
    // Out of budget mid-narrowing: hand back what we could not examine, so the
    // flat charge keeps tier 2 converging rather than letting a pathological
    // entry re-time-out on every remaining page. Checked BEFORE the singleton
    // verdict: out of budget, a timeout says nothing about the entry.
    if (applyRemainingMs() <= 0) {
      return unattributed();
    }
    // A single entry that times out alone IS the culprit.
    if (group.length <= 1) {
      return { entries: group, attributed: true, crashed: false };
    }

    const mid = Math.floor(group.length / 2);
    const halves = [group.slice(0, mid), group.slice(mid)].filter(
      (h) => h.length > 0
    );
    const slowHalves: Array<readonly LexiconEntry[]> = [];
    let crashed = false;

    for (const half of halves) {
      if (applyRemainingMs() <= 0) {
        return unattributed(crashed);
      }
      // The previous sub-run either timed out (worker terminated) or ran on a
      // worker we have already discarded, so always start clean.
      respawn();
      const deadline = deadlineForGroup(half.length);
      const sub = await runOnePage(
        worker,
        { type: 'LEXICON_MATCH', requestId, entries: half, pages: [page] },
        deadline,
        signal
      );
      if (sub.kind === 'aborted') {
        return unattributed(crashed);
      }
      if (sub.kind === 'ok') {
        keepHealthyRun(sub.progress, half, found, searched);
        continue;
      }
      if (sub.kind === 'crashed') {
        // A crash is not attributable to a pattern.
        crashed = true;
        continue;
      }
      if (budgetCut(deadline, half.length)) {
        return unattributed(crashed);
      }
      slowHalves.push(half);
    }

    if (crashed || slowHalves.length === halves.length) {
      return unattributed(crashed);
    }
    if (slowHalves.length === 0) {
      // Both halves passed on their own. The batch as a whole was too slow but
      // no part of it is, so there is nothing to name.
      return { entries: [], attributed: false, crashed: false };
    }
    return isolateSlowEntries(page, slowHalves[0]!, found, searched);
  };

  /**
   * Is a LONE entry's timeout the entry's fault? (VE-29686)
   *
   * A batch only reaches the singleton verdict in `isolateSlowEntries` after
   * that entry's OWN sub-run, on a fresh worker, timed out too: two
   * independent observations. A one-entry lexicon has had one, and one
   * timeout is also what a cold worker start, a main-thread stall or a dead
   * worker look like. Naming the entry on that writes an innocent lexicon into
   * the audit trail as the culprit, which is what the crash path refuses to do.
   *
   * So it asks two questions, cheapest first:
   *   1. Is the worker alive? A run with NO entries does no matching, so it can
   *      only miss its deadline if the worker cannot answer at all. If it does
   *      miss it, the worker is at fault: nobody is named, and the entry is
   *      charged like a crash so tier 2 does not name it four pages later.
   *   2. Does the entry time out AGAIN, on that live worker? Only then is it
   *      named. If it passes, its matches are kept and the page is searched.
   *
   * A timeout the apply budget cut short is the budget's, as for a half.
   *
   * THE PROBE IS RETRIED ONCE, and that is not belt-and-braces. Its deadline
   * is `PAGE_STARTUP_ALLOWANCE_MS`, a constant sized for worker boot — and the
   * worker it probes was respawned milliseconds ago, so the probe is racing
   * the exact cold start the allowance exists for. Lose that race and the
   * verdict is `workerAtFault`: safe (nobody is named) but useless, and a
   * genuinely runaway pattern then goes unnamed for the whole 30 s budget. A
   * second probe is not another roll of the same dice — by then the worker
   * chunk has been fetched and compiled, so a respawn boots from cache and a
   * live worker answers in single-digit ms. A worker that is actually dead
   * (the chunk 404s — this repo's documented prod-only breakage point) misses
   * both, which is the verdict we want. Picking a bigger constant instead
   * would be guessing at a distribution we cannot see.
   *
   * COST: a slow pattern, two deadlines and then named; a dead worker, three
   * deadlines a page (the page and both probes) until the apply budget stops
   * the run, naming nobody. Nothing here waits without a deadline, and every
   * step is gated on `applyRemainingMs`, so a worker that never answers cannot
   * stretch the run past `MATCH_BUDGET_APPLY_MS`. A main-thread stall during
   * the re-run can still read as a second timeout; two observations on a live
   * worker is the bar we accept.
   */
  const confirmLoneTimeout = async (
    page: LexiconMatchPage,
    group: readonly LexiconEntry[],
    /** The deadline the timed-out run actually had. */
    deadlineMs: number,
    found: LexiconMatchSpan[],
    searched: Set<string>
  ): Promise<Isolation> => {
    const unattributed = (crashed = false): Isolation => ({
      entries: group,
      attributed: false,
      crashed,
    });
    if (budgetCut(deadlineMs, group.length) || applyRemainingMs() <= 0) {
      return unattributed();
    }
    // The caller has already respawned after the timeout, so this is a fresh
    // worker. The probe carries no text: it tests answering, not matching.
    const sendProbe = (deadline: number) =>
      runOnePage(
        worker,
        {
          type: 'LEXICON_MATCH',
          requestId,
          entries: [],
          pages: [{ page: page.page, text: '' }],
        },
        deadline,
        signal
      );
    const probeDeadline = deadlineForGroup(0);
    let probe = await sendProbe(probeDeadline);
    // A FULL-deadline silence is a verdict already earned; the retry can only
    // confirm it, never erase it. Without this, a probe #2 the budget clipped
    // would clear `workerAtFault` that probe #1 established on its full 400 ms
    // — harmless today (a clipped probe means `budgetStop`, which charges the
    // same threshold either way) but an equivalence nobody should have to
    // re-derive.
    let workerSilent =
      probe.kind === 'timedOut' && !budgetCut(probeDeadline, 0);
    // Retry ONCE on a second fresh worker (see the header): the first probe may
    // have lost a cold-start race the second cannot. Gated on `budgetCut` as
    // well as the remaining budget — "a timeout the budget cut short carries no
    // inference" is this file's one rule, and a retry whose own deadline is
    // already clipped can only repeat the answer it was given. The deadline is
    // computed ONCE and both gated on and used, so the two cannot disagree.
    if (probe.kind === 'timedOut') {
      const retryDeadline = deadlineForGroup(0);
      if (!budgetCut(retryDeadline, 0)) {
        respawn();
        probe = await sendProbe(retryDeadline);
        workerSilent =
          workerSilent ||
          (probe.kind === 'timedOut' && !budgetCut(retryDeadline, 0));
      }
    }
    // `workerSilent` survives every exit below, including this one. A retry
    // that crashes says nothing about the full-deadline silence probe #1
    // already observed, and both verdicts charge the same threshold — but
    // "a retry can only confirm, never erase" is the invariant the flag exists
    // to hold, so it holds on both arms rather than one.
    if (probe.kind === 'crashed') {
      return { ...unattributed(true), workerAtFault: workerSilent };
    }
    if (probe.kind !== 'ok') {
      return { ...unattributed(), workerAtFault: workerSilent };
    }
    if (applyRemainingMs() <= 0) {
      return unattributed();
    }
    const deadline = deadlineForGroup(group.length);
    const again = await runOnePage(
      worker,
      { type: 'LEXICON_MATCH', requestId, entries: group, pages: [page] },
      deadline,
      signal
    );
    if (again.kind === 'ok') {
      keepHealthyRun(again.progress, group, found, searched);
      return {
        entries: [],
        attributed: false,
        crashed: false,
        workerLive: true,
      };
    }
    if (again.kind !== 'timedOut') {
      return unattributed(again.kind === 'crashed');
    }
    if (budgetCut(deadline, group.length)) {
      return unattributed();
    }
    return { entries: group, attributed: true, crashed: false };
  };

  try {
    for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
      const page = pages[pageIndex]!;
      if (signal?.aborted) {
        aborted = true;
        break;
      }
      if (now() - applyStart >= applyMs) {
        timedOut = true;
        unreachedPages = pages.slice(pageIndex).map((p) => p.page);
        break;
      }

      const active = entries.filter((e) => !abandoned.has(e.entryId));
      if (active.length === 0) {
        // Nothing left to search with; the rest of the document is unsearched.
        unreachedPages = pages.slice(pageIndex).map((p) => p.page);
        break;
      }

      const pageStart = now();
      // The page's allowance is tier 1 multiplied by the entries running on it.
      // Enforced as a REAL response deadline rather than measured after the
      // fact: a worker that goes silent (or is stuck inside a single
      // `RegExp.exec`) never sends a response at all, so a post-hoc elapsed
      // check could not observe it and the run would hang forever — exactly the
      // failure NFR-S3 exists to prevent.
      // Capped independently of `active.length`, and floored.
      //
      // `entryPageMs * active.length` alone made the tier hierarchy collapse in
      // both directions (review-gate B2). Upward: at the NFR-U5-7 ceiling of
      // 5,000 entries page 1's deadline became the whole 30 s apply budget, so
      // one page could eat the run and NFR-U5-6's "first results within 1 s"
      // was unenforceable. And because a timeout is charged as
      // `elapsed / active.length`, no single entry could ever cross the
      // tier-2 threshold, so BR-47 could not name the entry that caused it —
      // a bad pattern in a 100-entry lexicon returned zero matches for all 100
      // with nobody named. Downward: a one-entry lexicon got 50 ms to cover
      // structured-clone, worker startup and matching, and timed out on
      // nothing pathological.
      // How long a healthy batch may take before we stop waiting. Capped at
      // `DEADLINE_ENTRY_CAP` entries so one page cannot consume the whole
      // apply budget at the NFR-U5-7 ceiling.
      // How long we actually WAIT: tier 1 x the batch size (capped), floored by
      // the startup allowance, and never past what is left of the whole-apply
      // budget. Shared with the bisection so a narrowed sub-batch is judged by
      // the same rule as the batch it came from.
      const pageDeadlineMs = deadlineForGroup(active.length);

      const result = await runOnePage(
        worker,
        {
          type: 'LEXICON_MATCH',
          requestId,
          entries: active,
          pages: [page],
        },
        pageDeadlineMs,
        signal
      );

      if (result.kind === 'aborted') {
        aborted = true;
        break;
      }

      if (result.kind === 'crashed' || result.kind === 'timedOut') {
        // Either way the worker cannot be trusted to finish: terminate it (the
        // only way to stop a runaway `RegExp`) and respawn for the next page.
        respawn();

        if (result.kind === 'crashed') {
          // A crash used to be accounted for NOWHERE: the page entered neither
          // `pagesMatched` nor `timedOutPages`, no entry was charged, and
          // `timedOut` stayed false — so the caller built a CLEAN outcome, the
          // panel reported success and closed itself, and the record autosaved
          // as fully evaluated. A worker that dies on page 1 (a dense page, or
          // the worker chunk failing to load — a documented prod-only breakage
          // point in this repo) therefore produced a released document with
          // that page's matches missing and nothing anywhere saying so. That is
          // the silent under-redaction NFR-S3 exists to forbid, so a crash
          // names the page, charges the entries, and marks the run incomplete.
          //
          // Reported as its OWN cause rather than as a timeout (review-gate #3
          // BR-12). "Stopped early at the time limit" is the wrong sentence for
          // a worker that died, "Apply again" is the wrong remedy, and writing
          // a deployment defect into a FOIA audit trail as a performance event
          // hides the thing an operator needs to see.
          crashedPages.push(page.page);
          // Charged at exactly the tier-2 threshold, which under a `>` rule
          // can never abandon an entry by itself. A dead worker is a
          // deployment fault, not a slow pattern, and branding every entry in
          // the lexicon for it would name the innocent and hide the cause. The
          // page is still recorded as unsearched and `crashed` is still set —
          // that is what reports the gap.
          chargePageToActiveEntries(active, entryMeanPageMs);
          crashed = true;
        }

        if (result.kind === 'timedOut') {
          // Narrow the batch to the entries actually responsible, keeping the
          // matches any healthy sub-batch produces (review-gate #3 BR-8).
          // Charging the whole batch turned one bad pattern into a
          // whole-lexicon outage that named every entry and searched no
          // further pages.
          const recovered: LexiconMatchSpan[] = [];
          const searched = new Set<string>();
          // A lone entry is confirmed rather than bisected (VE-29686). It used to
          // be charged and left to tier 2, so a one-entry lexicon with a
          // runaway pattern was named only four deadline waits and four
          // unsearched pages later.
          const isolation: Isolation =
            active.length > 1
              ? await isolateSlowEntries(page, active, recovered, searched)
              : await confirmLoneTimeout(
                  page,
                  active,
                  pageDeadlineMs,
                  recovered,
                  searched
                );
          if (signal?.aborted) {
            aborted = true;
            break;
          }
          // The bisection leaves the worker in whatever state its LAST sub-run
          // left it (nit N4): a half that timed out terminated it, and nothing
          // inside the loop above starts another. Without this the next page
          // talks to a dead worker, sits out a full deadline, reports
          // `timedOut`, and bisects again — so one slow pattern costs a
          // deadline per remaining page instead of one. Kept when the last run
          // answered: discarding a warm worker makes every page cold-start.
          if (!isolation.workerLive) {
            respawn();
          }
          // WHAT THE PAGE ACTUALLY COST, not a flat tier-1 charge. The
          // bisection has already narrowed this to the entries responsible,
          // and they held the worker until its deadline elapsed — charging
          // them 50 ms for 400 ms of wall clock understates it, and under a
          // mean rule an entry charged exactly the threshold is never
          // abandoned, so a pattern that times out on every page would go
          // unnamed for ever.
          // Out of budget during the bisection: that is a budget stop too, even
          // if a crash is what gets recorded for the page.
          const budgetStop =
            applyRemainingMs() <= 0 || budgetCut(pageDeadlineMs, active.length);
          if (applyRemainingMs() <= 0) {
            timedOut = true;
          }
          // A crash, a worker that could not answer at all, or a stop the apply
          // budget made, is charged the threshold (never abandons on its own,
          // as on the main path); a timeout, the deadline its entries held.
          //
          // A BUDGET STOP CARRIES NO VERDICT (VE-29686). On a dead worker every
          // earlier page was charged exactly the threshold, so the full
          // deadline charged on the page where the budget ran out tipped the
          // mean over it, and tier 2 named the lone entry on page 38 of 200.
          // An entry the isolation actually NAMED keeps its full charge.
          chargePageToActiveEntries(
            isolation.entries,
            isolation.crashed ||
              isolation.workerAtFault === true ||
              (budgetStop && !isolation.attributed)
              ? entryMeanPageMs
              : pageDeadlineMs
          );
          // An entry that timed out ALONE is the culprit: name it now. Its mean
          // can stay under tier 2 (one timeout in eight pages is exactly 50 ms)
          // while this page counts as searched, which would be a silent gap.
          if (isolation.attributed) {
            for (const e of isolation.entries) {
              abandoned.add(e.entryId);
            }
          }
          // The recovered matches are real whatever the verdict.
          matches.push(...recovered);
          // Searched only if someone reported it and everyone else is a culprit
          // named FOR this page — not merely abandoned by the charge above.
          const named = new Set(
            isolation.attributed ? isolation.entries.map((e) => e.entryId) : []
          );
          const gap =
            searched.size === 0 ||
            active.some(
              (e) => !searched.has(e.entryId) && !named.has(e.entryId)
            );
          if (!gap) {
            pagesMatched.push(page.page);
            onProgress?.({
              type: 'LEXICON_MATCH_PROGRESS',
              requestId,
              page: page.page,
              matches: recovered,
              failedEntryIds: [],
            });
          } else if (isolation.crashed) {
            // Unattributed entries missed it: a PAGE gap, reported as one.
            crashedPages.push(page.page);
            crashed = true;
          } else {
            timedOutPages.push(page.page);
          }
        }
        continue;
      }

      const elapsed = now() - pageStart;

      // Charge each entry the worker's own compute time for it, so main-thread
      // stalls (a render, GC) cannot abandon a healthy entry and the expensive
      // one is the one charged. Without `entryMs`, the round trip, shared.
      const entryMs = result.progress.entryMs;
      const perEntry = active.length > 0 ? elapsed / active.length : 0;
      for (const entry of active) {
        chargeEntry(
          entry.entryId,
          entryMs ? measured(entryMs, entry.entryId) : perEntry
        );
      }

      for (const failedId of result.progress.failedEntryIds) {
        abandoned.add(failedId);
      }
      matches.push(...result.progress.matches);
      pagesMatched.push(page.page);
      onProgress?.(result.progress);
    }
  } finally {
    dispose();
  }

  return {
    matches,
    abandonedEntryIds: [...abandoned],
    timedOut: timedOut || timedOutPages.length > 0,
    crashed,
    pagesMatched,
    timedOutPages,
    crashedPages,
    ...(unreachedPages.length > 0 ? { unreachedPages } : {}),
    ...(aborted ? { aborted: true } : {}),
  };
}

type OnePageResult =
  | { kind: 'ok'; progress: LexiconMatchProgress }
  | { kind: 'crashed' }
  | { kind: 'timedOut' }
  | { kind: 'aborted' };

/**
 * Sends one page and resolves on its progress message, its error, or the
 * deadline — whichever comes first.
 *
 * The deadline is not optional. Without it a worker stuck inside a single
 * `RegExp.exec` never answers, and this promise never settles: the run hangs
 * with no budget able to observe it. That is the precise hang NFR-S3 forbids,
 * and it is why the caller terminates the worker on a `timedOut` result.
 *
 * Responses whose `requestId` does not match are DROPPED rather than trusted:
 * termination does not guarantee that an in-flight message is never delivered,
 * so a stale response must be identifiable. This is what `requestId` is for.
 *
 * An abort settles it immediately.
 */
function runOnePage(
  worker: MatchWorkerLike,
  request: LexiconMatchRequest,
  deadlineMs: number,
  signal?: AbortSignal
): Promise<OnePageResult> {
  return new Promise<OnePageResult>((resolve) => {
    if (signal?.aborted) {
      resolve({ kind: 'aborted' });
      return;
    }
    let settled = false;
    // Held in an object so it can be `const` while still being assigned after
    // `settle` closes over it.
    const deadline: { id?: ReturnType<typeof setTimeout> } = {};

    const onMessage = (event: MessageEvent<LexiconMatchResponse>) => {
      const response = event.data;
      if (!response || response.requestId !== request.requestId) {
        return;
      }
      if (response.type === 'LEXICON_MATCH_PROGRESS') {
        settle({ kind: 'ok', progress: response });
      }
    };
    const onError = () => settle({ kind: 'crashed' });
    const onAbort = () => settle({ kind: 'aborted' });

    const settle = (result: OnePageResult) => {
      if (settled) {
        return;
      }
      settled = true;
      if (deadline.id !== undefined) {
        clearTimeout(deadline.id);
      }
      // Detach: the worker outlives this page when it is reused, so leaving
      // the listeners attached leaked one pair per page (review-gate M3).
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
      signal?.removeEventListener('abort', onAbort);
      resolve(result);
    };

    deadline.id = setTimeout(() => settle({ kind: 'timedOut' }), deadlineMs);

    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);
    signal?.addEventListener('abort', onAbort);

    worker.postMessage(request);
  });
}

export type {
  LexiconMatchDone,
  LexiconMatchPage,
  LexiconMatchProgress,
  LexiconMatchSpan,
};
